import { ReactNode, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  MRT_RowData,
  MRT_SortingState,
  MRT_TableOptions,
  MRT_TableContainer as Table,
  useMaterialReactTable,
  MRT_ColumnDef,
  MRT_Row,
  MRT_TableInstance,
  MRT_Cell,
} from 'material-react-table';

// MUI
import { Typography, useTheme, useMediaQuery, Box, BoxProps } from '@mui/material';

// common
import NewPagination, { type NewPaginationProps } from 'common/components/NewPagination';

interface Props<T extends MRT_RowData = MRT_RowData> {
  paginationProps: NewPaginationProps;
  onSortingChange?: (value: MRT_SortingState) => void;
  renderRowActions?: (props: {
    isTablet: boolean;
    closeMenu?: () => void;
    row: MRT_Row<T>;
    cell?: MRT_Cell<T>;
    staticRowIndex?: number;
    table: MRT_TableInstance<T>;
  }) => ReactNode[];
  boxProps?: BoxProps;
  customFilterUi: ReactNode;
}

const NewMuiTable = <T extends MRT_RowData = MRT_RowData>(
  props: Props<T> & Omit<MRT_TableOptions<T>, 'onSortingChange' | 'renderRowActions'>
) => {
  const {
    boxProps,
    paginationProps,
    state,
    muiSearchTextFieldProps,
    muiTablePaperProps,
    muiTopToolbarProps,
    onSortingChange,
    muiTableBodyCellProps,
    muiTableHeadCellProps,
    enableRowVirtualization,
    renderBottomToolbarCustomActions,
    columns,
    renderRowActions,
    customFilterUi,
    ...rest
  } = props;

  const [sorting, setSort] = useState<MRT_SortingState>(state?.sorting || []);

  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down('laptop'));
  const intl = useIntl();

  const mappedColumns = useMemo<MRT_ColumnDef<T, any>[]>(() => {
    return [...columns].map((column) => ({
      enableSorting: false,
      ...column,
    }));
  }, [columns]);

  const renderActions = (params) => {
    const actions = renderRowActions!({ isTablet, ...params });
    return actions.map((item, index) => <Box key={index}>{item}</Box>);
  };

  const table = useMaterialReactTable<T>({
    columns: mappedColumns,
    enableColumnPinning: true,
    enableStickyHeader: true,
    enableFullScreenToggle: false,
    enableTopToolbar: false,
    columnFilterDisplayMode: 'custom',
    enableColumnActions: false,
    enableFilterMatchHighlighting: false,
    positionActionsColumn: 'last',
    paginationDisplayMode: 'pages',
    muiPaginationProps: {
      color: 'primary',
      shape: 'rounded',
      variant: 'outlined',
    },
    muiTableContainerProps: { sx: { maxHeight: '70dvh' } },
    initialState: { columnPinning: { right: ['mrt-row-actions'] } },
    localization: {
      rowsPerPage: intl.formatMessage({ id: 'rowsPerPage' }),
    },
    state: {
      density: 'compact',
      sorting,
      isLoading: false,
      ...state,
    },
    muiTableProps: {
      sx: {
        '& .Mui-TableBodyCell-DetailPanel': {
          border: 'none',
        },
        '& .MuiTableRow-footer': {
          bgcolor: 'grey.50',
        },
        '& .MuiTableCell-root[data-pinned="true"]:before': {
          backgroundColor: 'background.paper',
        },
      },
    },
    muiTableBodyCellProps: {
      align: 'center',
      sx: {
        minWidth: 'unset',
        whiteSpace: 'pre-wrap',
        lineHeight: 1,
        wordBreak: 'break-word',
      },
      size: 'small',
    },
    displayColumnDefOptions: {
      'mrt-row-expand': {
        size: 10,
        muiTableHeadCellProps: {
          sx: { maxWidth: 40 },
        },
        muiTableBodyCellProps: {
          sx: { maxWidth: 40 },
        },
      },
      'mrt-row-actions': {
        header: 'action',
        Header: isTablet ? <Box /> : <FormattedMessage id="action" />,
        size: isTablet ? 50 : 134,
      },
    },
    renderRowActions: isTablet
      ? undefined
      : (params) => (renderRowActions ? renderActions(params) : []),
    renderRowActionMenuItems: (params) => {
      return isTablet && renderRowActions ? renderActions(params) : [];
    },
    getRowId: ({ id }) => id,
    renderEmptyRowsFallback: () => (
      <Typography
        variant="body1"
        color="textSecondary"
        sx={(theme) => ({
          margin: 0,
          fontStyle: 'italic',
          height: 120,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          [theme.breakpoints.up('tablet')]: {
            height: 240,
          },
        })}
        component={'div'}
      >
        <FormattedMessage id="emptyData" />
      </Typography>
    ),
    muiDetailPanelProps: () => ({ sx: () => ({ p: 0 }) }),
    muiTableHeadCellProps: () => ({
      align: 'center',
      sx: {
        whiteSpace: 'pre-wrap',
        '& .Mui-TableHeadCell-Content-Wrapper': {
          whiteSpace: 'pre-wrap',
          lineHeight: 1,
        },
      },
      size: 'small',
    }),
    isMultiSortEvent: () => true,
    mrtTheme: (theme) => ({
      baseBackgroundColor: theme.palette.background.paper,
      draggingBorderColor: theme.palette.secondary.main,
    }),
    onSortingChange: (sortingUpdater: any) => {
      const isArray = typeof sortingUpdater === 'object' && Array.isArray(sortingUpdater);

      const newSortVal = isArray ? [] : sortingUpdater(sorting);
      setSort(newSortVal);
      if (onSortingChange) onSortingChange(newSortVal);
    },
    ...rest,
  });

  const count = table.getRowCount();

  return (
    <Box className={'flex flex-col overflow-hidden flex-1'} {...boxProps}>
      {customFilterUi}
      <Table table={table} />
      {paginationProps && <NewPagination {...paginationProps} count={count} />}
    </Box>
  );
};

export default NewMuiTable;
